import { z } from 'zod';

import type { QuizAnswers as RecordAnswers } from '@/pages/MyYachts/components/modals/QuizModal/quizModal.data.ts';
import { getVesselQuestionnaireAnswers } from '@/pages/MyYachts/components/modals/vesselQuestionnaire.data.ts';
import { homePortFromAnswer, withoutHomePortAnswer } from '@/store/vessel-marinas/home-port';
import type { HomePort, UpdateVesselDto, Vessel } from '@/store/vessels/vessel.dto';
import { DEFAULT_TIME_ZONE, getBrowserTimeZone, isValidTimeZone } from '@/utils/timezone';
import { round1, toCanonicalFt, toDisplayLength, type LengthUnit } from '@/utils/unitConversion';

export const vesselQuestionsAnswersSchema = z.object({
  draft_m: z.number().optional().catch(undefined),
  beam_m: z.number().optional().catch(undefined),
  guest_cabins: z.number().optional().catch(undefined),
  crew_count: z.number().optional().catch(undefined),
  country_built: z.string().optional().catch(undefined),
  home_port: z.string().optional().catch(undefined),
  engines: z.string().optional().catch(undefined),
  notes: z.string().optional().catch(undefined),
  cruising_areas: z
    .array(z.enum(['USA East Coast', 'USA West Coast', 'Bahamas & Caribbean']))
    .optional()
    .catch(undefined),
  usage_type: z.enum(['Private', 'Charter', 'Both']).optional().catch(undefined),
  flag_state: z.string().optional().catch(undefined),
  vessel_type: z
    .array(
      z.enum([
        'Motoryacht',
        'Sailing yacht',
        'Center Console',
        'Day Boat',
        'Trawler',
        'Sportfish Yacht',
        'Sailboat',
        'Powerboat',
        'Cruiser',
        'Commercial Vessel',
      ]),
    )
    .optional()
    .catch(undefined),
  hull_material: z
    .array(z.enum(['Fiberglass', 'Aluminum', 'Steel', 'Wood']))
    .optional()
    .catch(undefined),
  estimated_value: z.number().optional().catch(undefined),
  user_edited: z.array(z.string()).optional().catch(undefined),
});

export const vesselFormSchema = z.object({
  vesselName: z.string().min(1, 'Please enter a vessel name.'),
  vesselMake: z.string().optional(),
  vesselModel: z.string().optional(),
  yearBuilt: z
    .string()
    .optional()
    .refine((val) => !val || /^\d{4}$/.test(val), 'Please enter the year as 4 digits.'),
  lengthOverall: z
    .string()
    .optional()
    .refine(
      (val) => !val || /^\d+(\.\d+)?$/.test(val),
      'Please enter the length as a positive number.',
    ),
  countryRegistration: z.string().optional(),
  registrationNumber: z.string().optional(),
  hullId: z.string().optional(),
  description: z.string().optional(),
  // Same check the backend runs with time.LoadLocation, so a bad zone fails here, not as a 400.
  timezone: z.string().refine(isValidTimeZone, 'Please select a timezone.'),
  questions: vesselQuestionsAnswersSchema,
});

export type VesselFormValues = z.infer<typeof vesselFormSchema>;

export const VESSEL_DEFAULT_FORM_VALUES: VesselFormValues = {
  vesselName: '',
  vesselMake: '',
  vesselModel: '',
  yearBuilt: '',
  lengthOverall: '',
  countryRegistration: '',
  registrationNumber: '',
  hullId: '',
  description: '',
  // New vessels start from the creator's own zone; existing ones keep whatever is stored.
  timezone: getBrowserTimeZone(),
  questions: {},
};

export const vesselToFormValues = (
  vessel: Vessel | undefined,
  lengthUnit: LengthUnit = 'ft',
): VesselFormValues => ({
  vesselName: vessel?.vessel_name || '',
  vesselMake: vessel?.make || '',
  vesselModel: vessel?.model || '',
  yearBuilt: vessel?.year_built?.toString() || '',
  lengthOverall:
    vessel?.length_overall != null
      ? String(round1(toDisplayLength(vessel.length_overall, lengthUnit)))
      : '',
  // This form edits the flag as Country Registration: it shows the answer, as the vessel page
  // does, and saves to both.
  countryRegistration:
    getVesselQuestionnaireAnswers(vessel?.questions).flag_state ||
    vessel?.country_registration ||
    '',
  registrationNumber: vessel?.registration_number || '',
  hullId: vessel?.structure_number_value || '',
  description: vessel?.description || '',
  timezone: vessel?.timezone || DEFAULT_TIME_ZONE,
  questions: vesselQuestionsAnswersSchema.parse(
    withVesselRecordAnswers(vessel, getVesselQuestionnaireAnswers(vessel?.questions)),
  ),
});

export const formValuesToUpdatePayload = (
  values: VesselFormValues,
  /** The vessel's `home_port`: left out of the body, the backend erases it. */
  homePort: HomePort | null | undefined,
  lengthUnit: LengthUnit = 'ft',
): UpdateVesselDto => ({
  vessel_name: values.vesselName,
  make: values.vesselMake || undefined,
  model: values.vesselModel || undefined,
  year_built: values.yearBuilt ? parseInt(values.yearBuilt, 10) : undefined,
  length_overall: values.lengthOverall
    ? Math.round(toCanonicalFt(parseFloat(values.lengthOverall), lengthUnit))
    : undefined,
  country_registration: values.countryRegistration || undefined,
  registration_number: values.registrationNumber || undefined,
  structure_number_type: values.hullId ? 'hull_id' : undefined,
  structure_number_value: values.hullId || undefined,
  description: values.description?.trim() || undefined,
  home_port: homePortFromAnswer(homePort, values.questions.home_port),
  // Always sent — see UpdateVesselDto.timezone; omitting it resets the vessel to UTC.
  timezone: values.timezone || DEFAULT_TIME_ZONE,
  questions: Object.values(values.questions).some((value) =>
    Array.isArray(value) ? value.length > 0 : value !== undefined,
  )
    ? {
        version: 2,
        answers: withoutHomePortAnswer({
          ...values.questions,
          flag_state: values.countryRegistration || undefined,
        }),
      }
    : undefined,
});

const trimmedOrUndefined = (value?: string) => value?.trim() || undefined;

type VesselRecordFields = Pick<
  RecordAnswers,
  | 'make'
  | 'model'
  | 'year_built'
  | 'length_overall'
  | 'registration_number'
  | 'hull_id'
  | 'description'
  | 'home_port'
  | 'flag_state'
>;

export const withVesselRecordAnswers = <T extends object>(
  vessel: Vessel | null | undefined,
  answers: T,
): T & VesselRecordFields => {
  const current = answers as VesselRecordFields;

  return {
    ...answers,
    make: vessel?.make ?? current.make,
    model: vessel?.model ?? current.model,
    year_built: vessel?.year_built ?? current.year_built,
    length_overall: vessel?.length_overall ?? current.length_overall,
    registration_number: vessel?.registration_number ?? current.registration_number,
    hull_id: vessel?.structure_number_value ?? current.hull_id,
    description: vessel?.description ?? current.description,
    // `home_port` keeps it; an answer saved before it did shows until a save moves it there.
    home_port: vessel?.home_port?.name || current.home_port,
    // The answer wins over a flag stored before saves wrote it back to `country_registration`.
    flag_state: current.flag_state || vessel?.country_registration || undefined,
  };
};

/** The home port as stored, counting a name the questionnaire saved before `home_port` kept it. */
export const savedHomePort = (vessel: Vessel) =>
  vessel.home_port?.name
    ? vessel.home_port
    : homePortFromAnswer(
        vessel.home_port,
        getVesselQuestionnaireAnswers(vessel.questions).home_port,
      );

export const vesselUpdateFromAnswers = (
  vessel: Vessel,
  answers: VesselRecordFields,
): UpdateVesselDto => {
  const hullId = trimmedOrUndefined(answers.hull_id) ?? vessel.structure_number_value;

  return {
    vessel_name: vessel.vessel_name,
    make: trimmedOrUndefined(answers.make) ?? vessel.make,
    model: trimmedOrUndefined(answers.model) ?? vessel.model,
    year_built: answers.year_built ?? vessel.year_built,
    length_overall:
      answers.length_overall != null ? Math.round(answers.length_overall) : vessel.length_overall,
    // `''` clears the flag, so the stored one does not come back as the fallback.
    country_registration: answers.flag_state ?? vessel.country_registration,
    registration_number:
      trimmedOrUndefined(answers.registration_number) ?? vessel.registration_number,
    structure_number_type: hullId ? 'hull_id' : vessel.structure_number_type,
    structure_number_value: hullId,
    description: trimmedOrUndefined(answers.description) ?? vessel.description,
    // The picker saves a home port as it is picked; `answers.home_port` only names it, and
    // a name with no marina behind it — typed, or read off a document — is not saved.
    home_port: savedHomePort(vessel),
    timezone: vessel.timezone || DEFAULT_TIME_ZONE,
    questions: {
      version: 2,
      answers: withoutHomePortAnswer(vesselQuestionsAnswersSchema.parse(answers)),
    },
  };
};

export const recordAnswersToFormValues = (
  answers: VesselRecordFields,
  lengthUnit: LengthUnit = 'ft',
): Partial<VesselFormValues> => ({
  ...(trimmedOrUndefined(answers.make) ? { vesselMake: answers.make } : {}),
  ...(trimmedOrUndefined(answers.model) ? { vesselModel: answers.model } : {}),
  ...(answers.year_built != null ? { yearBuilt: String(answers.year_built) } : {}),
  ...(answers.length_overall != null
    ? { lengthOverall: String(round1(toDisplayLength(answers.length_overall, lengthUnit))) }
    : {}),
  ...(trimmedOrUndefined(answers.registration_number)
    ? { registrationNumber: answers.registration_number }
    : {}),
  ...(trimmedOrUndefined(answers.hull_id) ? { hullId: answers.hull_id } : {}),
  ...(trimmedOrUndefined(answers.description) ? { description: answers.description } : {}),
});

export const formValuesToRecordAnswers = (
  values: VesselFormValues,
  lengthUnit: LengthUnit = 'ft',
): VesselRecordFields => ({
  make: trimmedOrUndefined(values.vesselMake),
  model: trimmedOrUndefined(values.vesselModel),
  year_built: values.yearBuilt ? parseInt(values.yearBuilt, 10) : undefined,
  length_overall: values.lengthOverall
    ? Math.round(toCanonicalFt(parseFloat(values.lengthOverall), lengthUnit))
    : undefined,
  registration_number: trimmedOrUndefined(values.registrationNumber),
  hull_id: trimmedOrUndefined(values.hullId),
  description: trimmedOrUndefined(values.description),
});
